import type { RefObject } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { Text } from '@atlaskit/primitives/compiled/text';
import FadeIn from '@atlaskit/motion/fade-in';
import { fg as platform_fg } from '@atlaskit/platform-feature-flags/fg';
import Heading from '@atlaskit/heading/heading';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { TrackChangesPlugin } from '@atlaskit/editor-plugin-track-changes/track-changes-plugin-type';

import type { AIPlugin } from '@atlassian/editor-plugin-ai/aiPluginType';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { AIFC_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { fg } from '@confluence/feature-gating';

import { UNSAFE_UpdateOnChange as PopoverUpdateOnChange } from '@atlaskit/spotlight/update-on-change';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverContent } from '@atlaskit/spotlight/popover-content';

const i18n = defineMessages({
	AIStreamingSpotlightTitle: {
		id: 'onboarding-ai-first-create.ai-streaming.spotlight.title',
		defaultMessage: 'View changes or undo',
		description:
			'The text is shown as the heading of a spotlight tooltip that appears while AI is streaming generated content into the editor, informing the user they can review the changes or undo the AI-generated output.',
	},
	AIStreamingSpotlightDescription: {
		id: 'onboarding-ai-first-create.ai-streamin.spotlight.description',
		defaultMessage: 'Compare your edits and Rovo’s to a prior version and undo if needed.',
		description: 'Description text for AI streaming spotlight',
	},
	AIStreamingSpotlightCTA: {
		id: 'onboarding-ai-first-create.ai-streamin.spotlight.cta',
		defaultMessage: 'OK',
		description: 'CTA button text for AI streaming spotlight',
	},
});

export type EditorAPI = PublicPluginAPI<[AIPlugin, TrackChangesPlugin]>;
export type EditorAPIRef = RefObject<EditorAPI | null | undefined>;

export type AIStreamingSpotlightProps = {
	children: React.ReactNode;
	editorAPIRef?: EditorAPIRef;
};

export const AIStreamingSpotlight = ({
	children,
	editorAPIRef,
}: AIStreamingSpotlightProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const [isSpotlightActive, setIsSpotlightActive] = useState(false);

	const [streamingState, setStreamingState] = useState<'initial' | 'streaming' | 'completed'>(
		'initial',
	);

	const { isStreaming, isShowDiffAvailable } = useSharedPluginStateWithSelector(
		editorAPIRef?.current,
		['aiExperience', 'trackChanges'],
		(states) => {
			return {
				isStreaming: states?.aiExperienceState?.isStreaming,
				isShowDiffAvailable: states?.trackChangesState?.isShowDiffAvailable,
			};
		},
	);

	useEffect(() => {
		if (isStreaming) {
			if (streamingState === 'initial') {
				setStreamingState('streaming');
			}
		} else {
			if (streamingState === 'streaming' && isShowDiffAvailable) {
				setStreamingState('completed');
			} else {
				setStreamingState('initial');
			}
		}
	}, [isStreaming, isShowDiffAvailable, streamingState]);

	const { setOnboardingState } = useSetOnboardingState();

	const { data, loading, error } = useGetOnboardingState(
		Object.values(AIFC_ONBOARDING_STATE_KEYS),
		!fg('cc_aifc_ai_streaming_spotlight') || !fg('aifc_create_enabled'),
	);

	const { hasSeenTrackChangesStreamingSpotlight } = deserializeState(data);

	const handleSpotlightStart = useCallback(() => {
		setOnboardingState({
			key: AIFC_ONBOARDING_STATE_KEYS.hasSeenTrackChangesStreamingSpotlight,
			value: 'true',
		});

		setIsSpotlightActive(true);

		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'AIStreamingSpotlight',
				attributes: {
					source: 'onboarding-ai-first-create',
				},
			},
		}).fire();
	}, [createAnalyticsEvent, setOnboardingState]);

	const handleSpotlightStop = useCallback(() => {
		setIsSpotlightActive(false);
	}, []);

	const { useChoreographer } = useChoreographerAssets();

	const aiStreamingSpotlightChoreographer = useChoreographer('AIStreamingSpotlight', {
		start: handleSpotlightStart,
		stop: handleSpotlightStop,
	});

	const handleSpotlightInteraction = useCallback(
		(action: string, actionSubject: string, actionSubjectIdSuffix?: string) => {
			setIsSpotlightActive(false);

			void aiStreamingSpotlightChoreographer.stopMessage();

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action,
					actionSubject,
					actionSubjectId: `AIStreamingSpotlight${actionSubjectIdSuffix ?? ''}`,
					source: 'onboarding-ai-first-create',
				},
			}).fire();
		},
		[createAnalyticsEvent, aiStreamingSpotlightChoreographer],
	);

	const handleOnClick = useCallback(() => {
		handleSpotlightInteraction('clicked', 'button', 'CTA');
	}, [handleSpotlightInteraction]);

	const handleOnDismiss = useCallback(() => {
		handleSpotlightInteraction('clicked', 'button', 'Dismiss');
	}, [handleSpotlightInteraction]);

	useEffect(() => {
		if (
			!loading &&
			!error &&
			!hasSeenTrackChangesStreamingSpotlight &&
			!isStreaming &&
			isShowDiffAvailable &&
			streamingState === 'completed' &&
			fg('cc_aifc_ai_streaming_spotlight')
		) {
			void aiStreamingSpotlightChoreographer.startMessage();
		}
	}, [
		loading,
		error,
		hasSeenTrackChangesStreamingSpotlight,
		aiStreamingSpotlightChoreographer,
		isStreaming,
		isShowDiffAvailable,
		streamingState,
	]);

	if (!isSpotlightActive) {
		return <>{children}</>;
	}

	return (
		<PopoverProvider>
			<PopoverUpdateOnChange />
			<PopoverTarget>{children}</PopoverTarget>
			<PopoverContent
				dismiss={handleOnDismiss}
				isVisible={isSpotlightActive}
				placement="bottom-start"
			>
				{(() => {
					const spotlightCard = (
						<SpotlightCard testId="ai-streaming-spotlight">
							<SpotlightHeader>
								<SpotlightHeadline>
									<Heading as="span" size="small">
										{formatMessage(i18n.AIStreamingSpotlightTitle)}
									</Heading>
								</SpotlightHeadline>
								<SpotlightControls>
									<SpotlightDismissControl />
								</SpotlightControls>
							</SpotlightHeader>
							<SpotlightBody>
								<Text>{formatMessage(i18n.AIStreamingSpotlightDescription)}</Text>
							</SpotlightBody>
							<SpotlightFooter>
								<SpotlightActions>
									<SpotlightPrimaryAction onClick={handleOnClick}>
										{formatMessage(i18n.AIStreamingSpotlightCTA)}
									</SpotlightPrimaryAction>
								</SpotlightActions>
							</SpotlightFooter>
						</SpotlightCard>
					);
					return platform_fg('platform-dst-motion-uplift-spotlight') ? (
						spotlightCard
					) : (
						<FadeIn entranceDirection="left">
							{(props) => <div {...props}>{spotlightCard}</div>}
						</FadeIn>
					);
				})()}
			</PopoverContent>
		</PopoverProvider>
	);
};
